import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/shared/ui/accordion';
import { Checkbox } from '@/shared/ui/checkbox';
import { isCapabilityEnforced, listActions } from '@skewb-works/contracts';
import { ShieldCheck } from 'lucide-react';
import { useMemo } from 'react';
import { grantKey } from '../lib/rolePermissions';
import { groupSubjects, subjectLabel } from '../lib/subjectGroups';

/**
 * The matrix columns, one per action, in the Action source-of-truth order. Derived from
 * `listActions()` so adding/removing an action in `permissions.ts` re-flows the columns here
 * with no edit. Labels are the action value title-cased (e.g. `create` → `Create`).
 */
const MATRIX_ACTIONS: { action: string; label: string }[] = listActions().map((action: string) => ({
  action,
  label: action.charAt(0).toUpperCase() + action.slice(1),
}));

/**
 * Grid columns: a flexible label (`minmax(0, 1fr)` so it never forces overflow) plus one
 * column per action that shrinks to a tap-friendly min on mobile and caps at 4rem on wider
 * screens. Derived from the action count, so it tracks `MATRIX_ACTIONS` automatically.
 */
const gridTemplateColumns = `minmax(0, 1fr) repeat(${MATRIX_ACTIONS.length}, minmax(2.5rem, 4rem))`;

interface RoleMatrixProps {
  subjects: string[];
  /** Selected grants as `${action}::${subject}` keys. */
  selected: Set<string>;
  /**
   * Grant keys that are forced on by an implied dependency and can't be unticked (e.g.
   * `read Shift` while a `Team`/`Planner` grant is held). Rendered checked + disabled.
   */
  lockedKeys?: Set<string>;
  onToggle: (action: string, subject: string, checked: boolean) => void;
  disabled?: boolean;
}

/** The permission matrix: one row per subject, four CRUD columns, grouped by groupSubjects. */
export function RoleMatrix({
  subjects,
  selected,
  lockedKeys,
  onToggle,
  disabled,
}: RoleMatrixProps) {
  const groups = useMemo(() => groupSubjects(subjects), [subjects]);

  // Open every group so all permissions are visible at a glance. This is an uncontrolled Radix
  // `defaultValue`, read once at mount — the page gates mounting until subjects load, so `groups`
  // is already populated here.
  const openGroups = useMemo(() => groups.map((group) => group.label), [groups]);

  return (
    <div className="space-y-4">
      {/* The checkbox columns are right-anchored, so the header columns line up once this row's
          right edge matches the subject rows': AccordionItem border (1px) + content px-3 (12px) +
          row px-1 (4px) = 17px of inset. */}
      <div
        className="grid items-center gap-2 pl-1 pr-[17px] text-xs font-semibold text-muted-foreground"
        style={{ gridTemplateColumns }}
      >
        <span />
        {MATRIX_ACTIONS.map((col) => (
          <span key={col.action} className="text-center">
            {col.label}
          </span>
        ))}
      </div>

      <Accordion type="multiple" defaultValue={openGroups} className="space-y-3">
        {groups.map((group) => (
          <AccordionItem
            key={group.label}
            value={group.label}
            className="border rounded-lg overflow-hidden"
          >
            <AccordionTrigger className="px-3 py-2.5 hover:no-underline hover:bg-accent/50 [&[data-state=open]]:bg-accent/30">
              <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <ShieldCheck className="w-4 h-4 text-foreground" />
                {group.label}
              </span>
            </AccordionTrigger>
            <AccordionContent className="border-t px-3 pb-3 pt-2">
              <div className="space-y-1">
                {group.subjects.map((subject) => (
                  <div
                    key={subject}
                    className="grid items-center gap-2 px-1 py-1.5 rounded-md hover:bg-accent/50"
                    style={{ gridTemplateColumns }}
                  >
                    <span className="text-sm text-foreground">{subjectLabel(subject)}</span>
                    {MATRIX_ACTIONS.map((col) => {
                      // A pair no backend route enforces (e.g. create Job/WorkOrder, read
                      // User) would grant a no-op — show it disabled so it can't be
                      // selected (WRK-5474).
                      const enforced = isCapabilityEnforced(col.action, subject);
                      const key = grantKey(col.action, subject);
                      // A locked key is an implied dependency (e.g. `read Shift` while a
                      // Team/Planner grant is held): forced checked and not removable.
                      const locked = lockedKeys?.has(key) ?? false;
                      return (
                        <div key={col.action} className="flex justify-center">
                          <Checkbox
                            aria-label={`${col.action} ${subject}`}
                            checked={enforced && (locked || selected.has(key))}
                            disabled={disabled || !enforced || locked}
                            title={
                              locked
                                ? `${col.label} ${subject} is required while a Team or Scheduler permission is granted`
                                : enforced
                                  ? undefined
                                  : `${col.label} ${subject} is not an enforced permission`
                            }
                            onCheckedChange={(checked) =>
                              onToggle(col.action, subject, checked === true)
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
